import { useEffect, useMemo, useState } from 'react';
import { Transaction, PriceMap, PortfolioSnapshot, PricePoint } from '../types';
import { computeHoldings } from '../utils/calculations';
import { getAssetType } from '../utils/stocks';
import { cgGet, cgErrorMsg } from '../utils/coingecko';
import { walletValueSeries } from '../utils/walletTimeline';

/**
 * Accurate historical portfolio value for the Portfolio chart.
 *
 * For each day in the selected range it values the holdings the user actually
 * held on that day (reusing `computeHoldings` — NOT a fork) at that day's real
 * historical price, fetched per crypto coin from CoinGecko's `market_chart`
 * (the same source `useCoinHistory` uses). Non-crypto assets (metals / stocks),
 * and any coin whose history fails to load, fall back to the current price so a
 * single failure never blanks the whole line.
 *
 * The Demo CoinGecko plan only serves 365 days of daily history, so only the
 * bounded ranges below are supported; "All time" is gated in the UI.
 */

export type PortfolioRange = '90D' | '1Y' | 'ALL';

const RANGE_DAYS: Record<Exclude<PortfolioRange, 'ALL'>, number> = {
  '90D': 90,
  '1Y': 365,
};

const DAY_MS = 86_400_000;
const HIST_TIMEOUT = 15_000;

// Session-scoped cache + in-flight dedupe for the per-coin daily series. Historical
// daily prices don't change within a session, so we never refetch the same key.
const histCache = new Map<string, PricePoint[]>();
const inFlight = new Map<string, Promise<PricePoint[]>>();

async function fetchCoinDaily(coinId: string, days: number): Promise<PricePoint[]> {
  const key = `${coinId}_${days}`;
  const cached = histCache.get(key);
  if (cached) return cached;
  const existing = inFlight.get(key);
  if (existing) return existing;

  const promise = cgGet(`/coins/${coinId}/market_chart`, {
    // interval omitted so the Pro API auto-selects granularity (daily for >90d).
    params: { vs_currency: 'usd', days },
    timeout: HIST_TIMEOUT,
  })
    .then(({ data }) => {
      const pts: PricePoint[] = ((data as any).prices as [number, number][]).map(
        ([timestamp, price]) => ({ timestamp, price })
      );
      histCache.set(key, pts);
      return pts;
    })
    .finally(() => {
      inFlight.delete(key);
    });

  inFlight.set(key, promise);
  return promise;
}

/** UTC ISO date (YYYY-MM-DD) for a timestamp — matches how tx dates are stored/compared. */
function dateStr(ms: number): string {
  return new Date(ms).toISOString().slice(0, 10);
}

/** Forward-fill a coin's sparse daily points onto every day in `dayMsList`. */
function denseSeries(pts: PricePoint[], dayMsList: number[]): number[] {
  const sorted = [...pts].sort((a, b) => a.timestamp - b.timestamp);
  let i = 0;
  let last = sorted[0].price; // back-fill leading days with the earliest known price
  return dayMsList.map((dayMs) => {
    const dayEnd = dayMs + DAY_MS - 1;
    while (i < sorted.length && sorted[i].timestamp <= dayEnd) {
      last = sorted[i].price;
      i++;
    }
    return last;
  });
}

export interface PortfolioHistoryResult {
  history: PortfolioSnapshot[];
  loading: boolean;
  error: string | null;
}

/**
 * `wallet` (a walletTimeline wallet key, e.g. "Coinspot" or UNSPECIFIED_WALLET)
 * narrows the series to that one wallet's holdings. It only changes the COMPUTE
 * step: the per-coin price history is fetched for every coin the user has ever
 * held regardless, so switching wallets re-prices data already in memory and
 * never refetches.
 */
export function usePortfolioHistory(
  transactions: Transaction[],
  range: PortfolioRange,
  currentPrices: PriceMap,
  wallet: string | null = null
): PortfolioHistoryResult {
  const [pointsByCoin, setPointsByCoin] = useState<Record<string, PricePoint[]>>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Distinct crypto coins to fetch (metals/stocks have no market_chart source).
  const cryptoIds = useMemo(() => {
    const set = new Set<string>();
    for (const tx of transactions) {
      if (getAssetType(tx.coinId) === 'crypto') set.add(tx.coinId);
    }
    return Array.from(set).sort();
  }, [transactions]);

  const unionIds = useMemo(
    () => Array.from(new Set(transactions.map((t) => t.coinId))),
    [transactions]
  );

  // Stable identity for the transaction set (fetch/compute only when it truly changes).
  const txKey = useMemo(
    () => transactions.map((t) => `${t.id}:${t.date}:${t.type}:${t.amount}:${t.price}`).join('|'),
    [transactions]
  );

  const firstTxDate = useMemo(
    () => (transactions.length ? transactions.reduce((m, t) => (t.date < m ? t.date : m), transactions[0].date) : ''),
    [transactions]
  );

  const supported = range !== 'ALL';

  // ── Fetch: per-coin daily history for the selected range ──
  useEffect(() => {
    if (!supported || transactions.length === 0 || cryptoIds.length === 0) {
      setPointsByCoin({});
      setError(null);
      setLoading(false);
      return;
    }

    let cancelled = false;
    const days = RANGE_DAYS[range];
    setLoading(true);
    setError(null);

    Promise.all(
      cryptoIds.map((id) =>
        fetchCoinDaily(id, days)
          .then((pts) => [id, pts] as const)
          .catch(() => [id, null] as const)
      )
    ).then((entries) => {
      if (cancelled) return;
      const next: Record<string, PricePoint[]> = {};
      let anyOk = false;
      let anyFail = false;
      for (const [id, pts] of entries) {
        if (pts && pts.length) { next[id] = pts; anyOk = true; }
        else anyFail = true;
      }
      setPointsByCoin(next);
      // Only surface an error when nothing loaded; partial failures fall back silently.
      setError(!anyOk && anyFail ? cgErrorMsg(new Error('history unavailable')) : null);
      setLoading(false);
    });

    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [txKey, range, supported]);

  // Only the prices of coins the user holds, so an unrelated tick does not
  // recompute the series. Prices never trigger a refetch (the fetch is keyed on
  // the transaction set and range only).
  const pricesKey = useMemo(
    () => unionIds.map((id) => `${id}:${currentPrices[id]?.usd ?? 0}`).join('|'),
    [unionIds, currentPrices]
  );

  // ── Compute: daily portfolio value from the fetched series ──
  //
  // A memo, not an effect + state: derived synchronously, so the series always
  // matches the CURRENT range and wallet in the same render. As an effect, a
  // wallet switch painted one frame of the previous wallet's line under the new
  // wallet's name.
  const history = useMemo<PortfolioSnapshot[]>(() => {
    if (!supported || transactions.length === 0) { return []; }

    const todayMs = Date.parse(`${dateStr(Date.now())}T00:00:00Z`);
    const windowStartMs = todayMs - (RANGE_DAYS[range] - 1) * DAY_MS;
    const firstTxMs = firstTxDate ? Date.parse(`${firstTxDate}T00:00:00Z`) : windowStartMs;
    const startMs = Math.max(windowStartMs, firstTxMs);
    if (startMs > todayMs) { return []; }

    const dayMsList: number[] = [];
    for (let ms = startMs; ms <= todayMs; ms += DAY_MS) dayMsList.push(ms);
    const dayStrs = dayMsList.map(dateStr);

    // Dense per-coin price series aligned to dayMsList (crypto with loaded history only).
    const denseByCoin: Record<string, number[]> = {};
    for (const id of cryptoIds) {
      const pts = pointsByCoin[id];
      if (pts && pts.length) denseByCoin[id] = denseSeries(pts, dayMsList);
    }

    // Read directly (not via a ref): this memo re-runs only when pricesKey says a
    // relevant price changed, so the prices captured here are always current.
    const prices = currentPrices;

    // One wallet: its end-of-day balances, priced exactly like the whole-portfolio
    // line below (historical where loaded, live for today, else current).
    if (wallet !== null) {
      const lastDay = dayStrs.length - 1;
      return walletValueSeries(transactions, wallet, dayStrs, (id, d) => {
        const histUsd = d !== lastDay && denseByCoin[id] ? denseByCoin[id][d] : undefined;
        return histUsd ?? prices[id]?.usd ?? 0;
      });
    }

    const out: PortfolioSnapshot[] = dayStrs.map((day, d) => {
      const isToday = d === dayStrs.length - 1;
      const txUpTo = transactions.filter((t) => t.date <= day);

      const pm: PriceMap = {};
      for (const id of unionIds) {
        // Today's endpoint uses live prices so the line ends on the dashboard value.
        // Earlier days use historical prices where available, else the current price.
        const histUsd = !isToday && denseByCoin[id] ? denseByCoin[id][d] : undefined;
        const usd = histUsd ?? prices[id]?.usd ?? 0;
        pm[id] = { usd, usd_24h_change: 0 };
      }

      const value = computeHoldings(txUpTo, pm).reduce((s, h) => s + h.totalValue, 0);
      return { date: day, value };
    });

    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [txKey, range, supported, pointsByCoin, pricesKey, firstTxDate, wallet]);

  return { history, loading, error };
}
